import type { paymentServicesTypes } from '@makemydeal/dr-platform-types';
import { TAX_RATE_REGIONS } from './buildCategories';
import type { RetentionTaxRow, RetentionTaxRegion } from './retentionTaxTypes';

type JurisdictionRegion = Exclude<RetentionTaxRegion, 'total'>;

const REGION_FIELDS: Record<JurisdictionRegion, (keyof paymentServicesTypes.TaxItemBreakdown)[]> = {
    state: ['stateTax', 'stateTaxRate', 'stateJurisdictionCode', 'stateJurisdictionDescription'],
    county: ['countyTax', 'countyTaxRate', 'countyJurisdictionCode', 'countyJurisdictionDescription'],
    city: ['cityTax', 'cityTaxRate', 'cityJurisdictionCode', 'cityJurisdictionDescription'],
    local: ['localTax', 'localTaxRate']
};

const round = (value: number, digits: number): number => Number(value.toFixed(digits));

/**
 * Rebuilds one TaxItem, dropping only the overrides the manager unchecked. Kept overrides and
 * never-overridden base rates (e.g. a default state rate) survive; totals are recomputed from them.
 */
const buildPartialTaxItem = (
    source: paymentServicesTypes.TaxItem,
    keptRegions: JurisdictionRegion[],
    enablePercentTaxRates: boolean
): paymentServicesTypes.TaxItem => {
    const sourceBreakdown = (source.taxBreakdown ?? {}) as paymentServicesTypes.TaxItemBreakdown;
    const sourceManualChanges = source.manualChanges ?? {};
    const breakdown: paymentServicesTypes.TaxItemBreakdown = {};
    const manualChanges: paymentServicesTypes.ManualTaxItemChanges = {};

    let totalRate = 0;
    let totalAmount = 0;

    TAX_RATE_REGIONS.forEach((region) => {
        const isKept = keptRegions.includes(region);
        const isOverridden = Boolean(sourceManualChanges[`${region}TaxRate`]);
        if (!isKept && isOverridden) return;

        REGION_FIELDS[region].forEach((field) => {
            const value = sourceBreakdown[field];
            if (value != null) {
                (breakdown as Record<string, unknown>)[field] = value;
            }
        });

        const rate = sourceBreakdown[`${region}TaxRate`];
        if (rate != null) totalRate += rate;
        const amount = sourceBreakdown[`${region}Tax`];
        if (amount != null) totalAmount += amount;

        if (isKept) {
            manualChanges[`${region}TaxRate`] = true;
        }
    });

    return {
        ...source,
        taxRate: enablePercentTaxRates ? round(totalRate, 5) : round(totalRate / 100, 5),
        taxAmount: round(totalAmount, 2),
        taxBreakdown: breakdown,
        manualChanges
    };
};

/**
 * Recombines the checked retention tax rows back into real TaxItem objects. Rows are grouped by
 * their source tax item; region rows produce a partial item limited to the checked jurisdictions,
 * while a 'total' row keeps the whole source item unchanged.
 */
export const combineRetainedTaxItems = (
    rows: RetentionTaxRow[],
    enablePercentTaxRates: boolean = false
): paymentServicesTypes.TaxItem[] => {
    const groups = new Map<paymentServicesTypes.TaxItem, RetentionTaxRow[]>();

    rows.forEach((row) => {
        const existing = groups.get(row.taxItem);
        if (existing) {
            existing.push(row);
        } else {
            groups.set(row.taxItem, [row]);
        }
    });

    const result: paymentServicesTypes.TaxItem[] = [];

    groups.forEach((groupRows, source) => {
        const hasTotalRow = groupRows.some((row) => row.region === 'total');
        if (hasTotalRow) {
            // Modal rows carry the adjusted (percentage) rate; the store/payment expects the raw
            // fraction. Convert back so a kept whole-item override (e.g. MUT) isn't scaled by 100 twice.
            result.push(enablePercentTaxRates ? { ...source } : { ...source, taxRate: round((source.taxRate ?? 0) / 100, 5) });
            return;
        }

        const regions = groupRows.map((row) => row.region as JurisdictionRegion);
        result.push(buildPartialTaxItem(source, regions, enablePercentTaxRates));
    });

    return result;
};
